import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChildren,
  DestroyRef,
  EventEmitter,
  HostBinding,
  inject,
  input,
  Output,
  QueryList, Renderer2,
  signal,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MultiSelectFilterEvent, MultiSelectModule } from 'primeng/multiselect';
import { IconKey } from '../../../../../whitelabel/icon-key.enum';
import { IconPipe } from '../../../../shared/pipes/icon.pipe';
import { SelectedItemsConfiguration } from '../../../contract/selected-items-configuration.contract';
import { OspTemplateDirective } from '../../../directives/osp-template.directive';
import { CoreControlDirective } from '../../../utils/core-control.directive';
import { SelectPanelCustomHeaderComponent } from '../tree-select/components/select-panel-custom-header/select-panel-custom-header.component';
import { SelectedItemsTemplateComponent } from './components/selected-items-template/selected-items-template.component';

export type SelectOption = { label: string; value: string };
export type SelectOptionLabel<T, TChildren> = Extract<keyof T | keyof TChildren, string>;
export type SelectOptionValue<T, TChildren> = Extract<keyof T | keyof TChildren, string>;

@Component({
  selector: 'osp-select',
  standalone: true,
  imports: [
    MultiSelectModule,
    ReactiveFormsModule,
    NgTemplateOutlet,
    SelectedItemsTemplateComponent,
    IconPipe,
    SelectPanelCustomHeaderComponent,
  ],
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  providers: CoreControlDirective.coreControlProvider(SelectComponent),
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent<T extends object | { items?: TChildren[] }, TChildren = T>
  extends CoreControlDirective
  implements AfterContentInit {
  private destroyRef = inject(DestroyRef);
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  private tooltipElement: HTMLElement | null = null;

  @ContentChildren(OspTemplateDirective) private templates!: QueryList<OspTemplateDirective>;
  @HostBinding('class.osp-select') protected hostCssClassEnabled = true;

  static readonly templateNameGroup = 'group';
  static readonly templateNamePreHeader = 'preheader';

  customTooltipClass = input<string>('');
  optionLabel = input<SelectOptionLabel<T, TChildren>>('label' as SelectOptionLabel<T, TChildren>);
  optionValue = input<SelectOptionValue<T, TChildren>>('value' as SelectOptionValue<T, TChildren>);
  options = input<T[]>();
  placeholder = input<string>();
  showClear = input(true);
  selectionLimit = input<number | undefined>();
  emptyFilterMessage = input<string>('');
  appendTo = input<string>('body');
  filter = input(true);
  $filterDebounceTime = input(0, { alias: 'filterDebounceTime' });
  group = input(false);
  $loading = input(false, { alias: 'loading' });
  selectedItemsConfiguration = input<SelectedItemsConfiguration>();
  isDisabledForEditing = input<boolean>(false);
  isAllowedToSeeData = input<boolean>(true);
  $maxSelectedLabels = input<number | null | undefined>(undefined, { alias: 'maxSelectedLabels' });
  $showToggleAll = input(true, { alias: 'showToggleAll' });
  $showHeader = input(true, { alias: 'showHeader' });
  $virtualScroll = input<boolean | undefined>(undefined, { alias: 'virtualScroll' });
  $virtualScrollItemSize = input<number | undefined>(undefined, { alias: 'virtualScrollItemSize' });
  $filterInputAutoFocus = input(true, { alias: 'filterInputAutoFocus' });

  @Output() onFilter = new EventEmitter<MultiSelectFilterEvent>();

  $preHeaderTmpl = signal<TemplateRef<unknown> | null>(null);
  $groupTmpl = signal<TemplateRef<unknown> | null>(null);
  $panelStyleClass = computed(() => this.getPanelStyleClass());
  $isAllSelected = computed(() => {
    const value = (this.$controlValue() ?? []) as string[];
    if (value.length !== 0 && value.length === (this.options()?.length ?? 0)) {
      const values = new Set(value);
      const key = this.optionValue() as keyof T;
      return this.options()?.every(option => values.has(option[key] as string)) ?? false;
    }
    return false;
  });

  protected readonly IconKey = IconKey;

  ngAfterContentInit(): void {
    this.subscribeToContentChanges();
  }

  handleFilterChange(event: MultiSelectFilterEvent) {
    this.onFilter.emit(event);
  }

  handleSelectAll(select: boolean) {
    const patchValue = select ? this.options()?.map(option => option[this.optionValue() as keyof T]) : [];
    this.control().patchValue(patchValue);
  }

  addTooltipClass(): void {
    if (this.customTooltipClass()) {
      setTimeout(() => {
        this.tooltipElement = this.document.querySelector('.p-tooltip');
        if (this.tooltipElement) {
          this.renderer.addClass(this.tooltipElement, this.customTooltipClass());
        }
      }, 0);
    }
  }

  removeTooltipClass(): void {
    if (this.customTooltipClass() && this.tooltipElement) {
      this.renderer.removeClass(this.tooltipElement, this.customTooltipClass());
      this.tooltipElement = null;
    }
  }

  protected toKeyboardEvent(event: Event): KeyboardEvent {
    return event as KeyboardEvent;
  }

  private subscribeToContentChanges(): void {
    this.updateTemplates();
    this.templates.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updateTemplates());
  }

  private updateTemplates(): void {
    this.templates.forEach(({ name, template }) => {
      if (name === SelectComponent.templateNamePreHeader) {
        this.$preHeaderTmpl.set(template);
      }
      if (name === SelectComponent.templateNameGroup) {
        this.$groupTmpl.set(template);
      }
    });
  }

  private getPanelStyleClass(): string {
    return [this.$preHeaderTmpl() ? 'pre-header' : ''].filter(Boolean).join(' ');
  }

}
